import { CompRef, DocumentServicesObject } from '@wix/document-services-types';
import { SLOT_ID_DIVIDER } from '../constants';
import {
  WidgetPointer,
  WidgetSlot,
  WidgetSlotPosition,
  SlotDefinition,
} from '@wix/editor-platform-host-integration-apis';
import { PluginHostApiImplementation } from '../types';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import {
  createWidgetPluginComponentDefinition,
  filterPublicSlots,
  getPluginInfo,
  getSlotDefinitions,
} from '../utils';
import { SlotPointer } from '../slotPointer';
import { getAppData } from '../utils/getAppData';

function addWidgetSlotData(
  documentServicesAPI: DocumentServicesObject,
  hostCompRef: CompRef,
  slotDefinitions: SlotDefinition[],
): WidgetSlot[] {
  return slotDefinitions.map((defintion) => {
    let populatedWidth;
    try {
      populatedWidth = defintion.innerElementPath
        ? documentServicesAPI.components.innerElements.slots.get(
            hostCompRef,
            defintion.innerElementPath,
            // @ts-expect-error
            defintion.name,
          )
        : // @ts-expect-error
          documentServicesAPI.components.slots.get(hostCompRef, defintion.name);
    } catch (error) {
      console.log({ error, defintion }); // TODO remove when implemented on DM
    }
    const pluginInfo = populatedWidth?.slots?.content
      ? getPluginInfo(
          documentServicesAPI,
          getAppData(documentServicesAPI, {
            id: populatedWidth?.slots?.content,
            type: hostCompRef.type,
          }),
        )
      : undefined;
    const result = {
      compRef: {
        id: 'not_supported_for_builder',
        type: 'DESKTOP',
      } as CompRef,
      pluginInfo,
      hostCompRef,
      id: defintion.id,
      role: defintion.name,
      placement: {
        appDefinitionId: defintion.appDefinitionId,
        widgetId: defintion.widgetId,
        slotId: defintion.id,
      },
    };
    return result;
  });
}

export function createBuilderHostAPI(
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
): PluginHostApiImplementation {
  return {
    getSlotPlaceholderDisplayName(_compRef: CompRef): string | undefined {
      throw new Error('Not implemented');
    },

    getWidgetHostRefBySlotContainer(slotPointer: SlotPointer): CompRef {
      return slotPointer.toBuilder().hostRef;
    },
    getWidgetSlot(
      slotPointer: SlotPointer,
      { includePrivate }: { includePrivate: boolean },
    ): WidgetSlot {
      const hostRef = this.getWidgetHostRefBySlotContainer(slotPointer);
      const allSlots = this.getWidgetSlots(hostRef, { includePrivate }); // TODO implement
      const slot = allSlots.find(
        (sl) => sl.id === slotPointer.toBuilder().slotId,
      );
      if (!slot) {
        throw new Error(`Slot not found for ${slotPointer.toBuilder().slotId}`);
      }
      return slot;
    },

    getWidgetSlots(
      widgetCompRef: CompRef,
      options: { includePrivate: boolean },
    ): WidgetSlot[] {
      const slotDefinitions = getSlotDefinitions(
        documentServicesAPI,
        widgetCompRef,
        experimentsAPI,
      );

      const filteredSlotDefinitions = options.includePrivate
        ? slotDefinitions
        : filterPublicSlots(slotDefinitions);

      return addWidgetSlotData(
        documentServicesAPI,
        widgetCompRef,
        filteredSlotDefinitions,
      );
    },

    getWidgetHostRef(_compRef: CompRef): CompRef {
      throw new Error('Not implemented');
    },

    async populateWidgetPluginToSlot(
      slotPointer: SlotPointer,
      widgetPointer: WidgetPointer,
    ): Promise<CompRef> {
      const path = slotPointer.toBuilder();
      const appData = documentServicesAPI.tpa.app.getDataByAppDefId(
        widgetPointer.appDefinitionId,
      );

      const widgetComponent = await createWidgetPluginComponentDefinition(
        documentServicesAPI,
        widgetPointer,
        appData.applicationId!,
      );

      if (typeof path.innerElementPath === 'undefined') {
        return documentServicesAPI.components.slots.populate(
          path.hostRef,
          path.slotKeyName,
          widgetComponent,
        );
      }

      return documentServicesAPI.components.innerElements.slots.populate(
        path.hostRef,
        path.innerElementPath,
        path.slotKeyName,
        widgetComponent,
      ) as CompRef;
    },

    uninstallWidgetPlugin(slotPointer: SlotPointer): void {
      const path = slotPointer.toBuilder();

      if (typeof path.innerElementPath === 'undefined') {
        return documentServicesAPI.components.slots.remove(
          path.hostRef,
          path.slotKeyName,
        );
      }
      // @ts-expect-error
      return documentServicesAPI.components.innerElements.slots.remove(
        path.hostRef,
        path.innerElementPath,
        path.slotKeyName,
      );
    },
    getWidgetPointer(slotPointer: SlotPointer): WidgetPointer | undefined {
      const path = slotPointer.toBuilder();
      let populatedWidth;
      try {
        populatedWidth = path.innerElementPath
          ? documentServicesAPI.components.innerElements.slots.get(
              path.hostRef,
              path.innerElementPath,
              // @ts-expect-error
              path.slotKeyName,
            )
          : // @ts-expect-error
            documentServicesAPI.components.slots.get(
              path.hostRef,
              path.slotKeyName,
            );
      } catch (error) {
        console.log({ error, path }); // TODO remove when implemented on DM
      }

      if (!populatedWidth) {
        return undefined;
      }

      const { appDefinitionId, widgetId } = getAppData(documentServicesAPI, {
        id: populatedWidth?.slots?.content,
        type: path.hostRef.type,
      });

      return { appDefinitionId, widgetId };
    },

    getSlotPlaceholderRefByContentRef(
      _widgetPluginCompRef: CompRef,
    ): CompRef | undefined {
      throw new Error('Not implemented');
    },

    isSlotPublic(_slotComponentRef: CompRef): boolean {
      throw new Error('Not implemented');
    },

    getSlotPosition(_compRef: CompRef): WidgetSlotPosition | null {
      throw new Error('Not implemented');
    },

    getSlotContentRef(_slotPointer: SlotPointer): CompRef | undefined {
      throw new Error('Not implemented');
    },

    getCompRefForSlotSelection(_slotPointer: SlotPointer): CompRef | undefined {
      throw new Error('Not implemented');
    },

    hasWidgetSlot(_widgetCompRef: CompRef): boolean {
      throw new Error('Not implemented');
    },

    async highlightSlot(_slotPointer: SlotPointer): Promise<void> {
      throw new Error('Not implemented');
    },

    unhighlightSlot(_slotPointer: SlotPointer): void {
      throw new Error('Not implemented');
    },

    isPluginComponent(_compRef: CompRef): boolean {
      throw new Error('Not implemented');
    },

    isWidgetSlotPopulated(_slotPointer: SlotPointer): boolean {
      throw new Error('Not implemented');
    },
  };
}
